import {
  ErrorHandler,
  LOCALE_ID,
  enableProdMode,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
} from "@angular/core";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import {
  PreloadAllModules,
  RouteReuseStrategy,
  provideRouter,
  withPreloading,
} from "@angular/router";
import {
  IonicModule,
  IonicRouteStrategy,
  isPlatform,
} from "@ionic/angular";
import { iosTransitionAnimation, mdTransitionAnimation } from "./transitions";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { localStorageSync } from "ngrx-store-localstorage";
import { routes } from "./app/home/home.routes";
import { AppComponent } from "./app/app.component";
import { environment } from "./environments/environment";
import { ActionReducer, MetaReducer, StoreModule } from "@ngrx/store";
import {
  calendarFeatureKey,
  calendarReducer,
} from "./app/calendar/ngrx/calendar.reducers";
import { EffectsModule } from "@ngrx/effects";
import { CalendarEffects } from "./app/calendar/ngrx/calendar.effects";
import { ROOT_REDUCERS } from "./app/reducers";
import { StudentEffects } from "./app/students/ngrx/students.effects";
import { CustomerEffects } from "./app/auth/ngrx/customer.effects";
import {
  studentFeatureKey,
  studentReducer,
} from "./app/students/ngrx/students.reducers";
import {
  customerFeatureKey,
  customerReducer,
} from "./app/auth/ngrx/customer.reducers";
import {
  settingFeatureKey,
  settingReducer,
} from "./app/settings/ngrx/settings.reducers";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
  invoiceFeatureKey,
  invoiceReducer,
} from "./app/office/invoice-ngrx/invoices.reducers";
import { InvoiceEffects } from "./app/office/invoice-ngrx/invoices.effects";
import { SettingEffects } from "./app/settings/ngrx/settings.effects";
import { MaskitoModule } from "@maskito/angular";
import localeDe from "@angular/common/locales/de-CH";
import { registerLocaleData } from "@angular/common";
import { provideServiceWorker } from "@angular/service-worker";
import {
  paymentFeatureKey,
  paymentReducer,
} from "./app/office/payment-ngrx/payments.reducers";
import { PaymentEffects } from "./app/office/payment-ngrx/payments.effects";
import { defineCustomElements } from "@ionic/pwa-elements/loader";
import { NgCircleProgressModule } from "ng-circle-progress";
import {
  trackingFeatureKey,
  trackingReducer,
} from "./app/tracking/ngrx/tracking.reducers";
import { TrackingEffects } from "./app/tracking/ngrx/tracking.effects";
import {
  resourceReducer,
  resourcesFeatureKey,
} from "./app/calendar/ngrx/resources.reducers";
import {
  reportFeatureKey,
  reportReducer,
} from "./app/report-generation/ngrx/reports.reducers";
import { ReportEffects } from "./app/report-generation/ngrx/reports.effects";
import { IonicStorageModule } from "@ionic/storage-angular";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { serverErrorInterceptor } from "./app/core/error/global-error-interceptor";
import { ErrorEffects } from "./app/core/error/ngrx/error.effects";
import { JoyrideModule } from "ngx-joyride";
import {
  productFeatureKey,
  productReducer,
} from "./app/products/ngrx/products.reducers";
import { ProductEffects } from "./app/products/ngrx/products.effects";
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular'
import { BugsnagService } from './app/core/services/bugsnag.service'
import { AppInitializerService } from './app/core/services/app-initializer.service'

registerLocaleData(localeDe);
defineCustomElements(window);
if (environment.production) {
  enableProdMode();
}

export function localStorageSyncReducer(
  reducer: ActionReducer<any>
): ActionReducer<any> {
  return localStorageSync({
    rehydrate: true,
    keys: [
      { appointments: ["entities", "ids", "calendarView"] },
      { customer: ["customer", "entitlements"] },
      { students: ["entities", "ids"] },
      { products: ["entities", "ids"] },
      { invoices: ["entities", "ids", "invoiceFilter"] },
      { payments: ["entities", "ids"] },
      {
        setting: [
          "services",
          "subscriptions",
          "educationBodies",
          "educationCategories",
          "educationTitles",
        ],
      },
      { resources: ["entities", "ids"] },
    ],
    removeOnUndefined: true,
  })(reducer);
}
const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

const getConfig = () => {
  if (isPlatform("ios")) {
    return {
      navAnimation: iosTransitionAnimation,
    }
  } else if (isPlatform("android")) {
    return {
      navAnimation: mdTransitionAnimation,
    }
  }
};

export function errorHandlerFactory(bugsnagService: BugsnagService) {
  return new BugsnagErrorHandler(bugsnagService.client);
}

export function initializeAppFactory(appInitializer: AppInitializerService) {
  return () => appInitializer.initializeApp();
}

bootstrapApplication(AppComponent, {
  providers: [
    {
      provide: ErrorHandler,
      useFactory: errorHandlerFactory,
      deps: [BugsnagService]
    },
    { provide: LOCALE_ID, useValue: "de-CH" },
    importProvidersFrom(
      JoyrideModule.forRoot(),
      BrowserAnimationsModule,
      BrowserModule,
      MaskitoModule,
      StoreModule.forFeature(calendarFeatureKey, calendarReducer),
      StoreModule.forFeature(studentFeatureKey, studentReducer),
      StoreModule.forFeature(invoiceFeatureKey, invoiceReducer),
      StoreModule.forFeature(customerFeatureKey, customerReducer),
      StoreModule.forFeature(paymentFeatureKey, paymentReducer),
      StoreModule.forFeature(settingFeatureKey, settingReducer),
      StoreModule.forFeature(trackingFeatureKey, trackingReducer),
      StoreModule.forFeature(resourcesFeatureKey, resourceReducer),
      StoreModule.forFeature(reportFeatureKey, reportReducer),
      StoreModule.forFeature(productFeatureKey, productReducer),
      NgCircleProgressModule.forRoot({
        radius: 60,
        outerStrokeWidth: 10,
        innerStrokeWidth: 5,
        outerStrokeColor: "#78C000",
        innerStrokeColor: "#C7E596",
        animationDuration: 300,
      }),
      EffectsModule.forRoot([]),
      EffectsModule.forFeature([
        CalendarEffects,
        StudentEffects,
        InvoiceEffects,
        ProductEffects,
        CustomerEffects,
        PaymentEffects,
        SettingEffects,
        TrackingEffects,
        ReportEffects,
        ErrorEffects,
      ]),
      StoreModule.forRoot(ROOT_REDUCERS, {
        metaReducers,
      }),
      IonicStorageModule.forRoot()
    ),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
    }),
    provideHttpClient(withInterceptors([serverErrorInterceptor])),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    importProvidersFrom(IonicModule.forRoot(getConfig())),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideServiceWorker("ngsw-worker.js", {
      enabled: !isDevMode(),
      registrationStrategy: "registerWhenStable:30000",
    }),
    provideZoneChangeDetection({eventCoalescing: true}),
  ],
});
